import React, { useCallback, useEffect, useState } from 'react';
import {
  typesService,
  HardwareFieldDefinition,
  HardwareFieldDefinitionInput,
  ApplySchemaResult,
  FieldGrouping,
} from '../../services/typesService';
import { getTypeBadgeColor } from '../../utils/fieldTypes';
import { useI18n } from '../../contexts/I18nContext';

interface HardwareSchemaJsonEditorProps {
  hardwareTypeId: number;
  hardwareTypeCode: string;
  canEdit: boolean;
  groupings?: FieldGrouping[];
  resolveGroupingInput?: (raw: string) => Promise<number | undefined>;
  onSchemaChanged: () => void;
  onClose: () => void;
}

const VALUE_TYPES = [
  'string', 'number', 'boolean', 'password', 'url',
  'hostname', 'port', 'select', 'json', 'array',
] as const;

interface EditableField {
  _key: string;            // stable React key
  field_name: string;
  display_name: string;
  value_type: string;
  default_value: string;
  is_required: boolean;
  expose_on_wire: boolean;
  wire_root: boolean;
  grouping_id: number | null;
  _groupText: string;      // editable group name (resolved on apply)
  help_text: string;
  display_order: number;
}

let _nextKey = 0;
const makeKey = () => `f_${++_nextKey}`;

const fromServerField = (f: HardwareFieldDefinition, grps: FieldGrouping[]): EditableField => ({
  _key: makeKey(),
  field_name: f.field_name,
  display_name: f.display_name,
  value_type: f.value_type,
  default_value: f.default_value ?? '',
  is_required: f.is_required,
  expose_on_wire: !!f.expose_on_wire,
  wire_root: !!f.wire_root,
  grouping_id: f.grouping_id ?? null,
  _groupText: grps.find(g => g.id === f.grouping_id)?.name ?? '',
  help_text: f.help_text ?? '',
  display_order: f.display_order ?? 0,
});

const toInput = (f: EditableField, grps: FieldGrouping[]): HardwareFieldDefinitionInput => {
  const out: Record<string, unknown> = {
    field_name: f.field_name,
    display_name: f.display_name,
    value_type: f.value_type,
  };
  if (f.default_value) out.default_value = f.default_value;
  if (f.is_required) out.is_required = true;
  if (f.expose_on_wire) out.expose_on_wire = true;
  if (f.wire_root) out.wire_root = true;
  // Always include grouping_id so clearing a group is sent as null
  out.grouping_id = f.grouping_id;
  if (f.grouping_id !== null) {
    const grp = grps.find(g => g.id === f.grouping_id);
    if (grp) out.group = grp.name;
  }
  if (f.help_text) out.help_text = f.help_text;
  if (f.display_order) out.display_order = f.display_order;
  return out as unknown as HardwareFieldDefinitionInput;
};

const emptyField = (): EditableField => ({
  _key: makeKey(),
  field_name: '',
  display_name: '',
  value_type: 'string',
  default_value: '',
  is_required: false,
  expose_on_wire: false,
  wire_root: false,
  grouping_id: null,
  _groupText: '',
  help_text: '',
  display_order: 0,
});

const HardwareSchemaJsonEditor: React.FC<HardwareSchemaJsonEditorProps> = ({
  hardwareTypeId,
  hardwareTypeCode,
  canEdit,
  groupings = [],
  resolveGroupingInput,
  onSchemaChanged,
  onClose,
}) => {
  const { t } = useI18n();
  const [fields, setFields] = useState<EditableField[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [deleteMissing, setDeleteMissing] = useState(false);
  const [lastResult, setLastResult] = useState<ApplySchemaResult | null>(null);
  const [rawMode, setRawMode] = useState(false);
  const [rawJson, setRawJson] = useState('');
  const [parseError, setParseError] = useState<string | null>(null);

  const loadFromServer = useCallback(async () => {
    setLoading(true);
    setServerError(null);
    try {
      const response = await typesService.getHardwareFieldDefinitions(hardwareTypeId);
      const loaded = response.items.map(f => fromServerField(f, groupings));
      setFields(loaded);
      setLastResult(null);
    } catch (err) {
      setServerError(err instanceof Error ? err.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [hardwareTypeId, groupings]);

  useEffect(() => { loadFromServer(); }, [loadFromServer]);

  // Sync fields → rawJson when switching to raw mode
  useEffect(() => {
    if (rawMode) {
      setRawJson(JSON.stringify(fields.map(f => toInput(f, groupings)), null, 2));
      setParseError(null);
    }
  }, [rawMode]); // eslint-disable-line react-hooks/exhaustive-deps

  /** Resolve a "group" string field to grouping_id in parsed JSON items */
  const resolveGroupNames = (items: Record<string, unknown>[]): Record<string, unknown>[] => {
    return items.map(item => {
      if (item.group && typeof item.group === 'string' && !item.grouping_id) {
        const name = (item.group as string).toLowerCase().trim();
        const match = groupings.find(g =>
          g.name.toLowerCase() === name || g.code.toLowerCase() === name
        );
        if (match) {
          return { ...item, grouping_id: match.id };
        }
      }
      return item;
    });
  };

  const updateField = (key: string, patch: Partial<EditableField>) => {
    setFields(prev => prev.map(f => f._key === key ? { ...f, ...patch } : f));
  };

  const removeField = (key: string) => {
    setFields(prev => prev.filter(f => f._key !== key));
  };

  const addField = () => {
    setFields(prev => [...prev, emptyField()]);
  };

  const moveField = (key: string, dir: -1 | 1) => {
    setFields(prev => {
      const idx = prev.findIndex(f => f._key === key);
      if (idx < 0) return prev;
      const target = idx + dir;
      if (target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      [next[idx], next[target]] = [next[target], next[idx]];
      return next;
    });
  };

  const handleApply = async () => {
    let inputs: HardwareFieldDefinitionInput[];

    if (rawMode) {
      try {
        const parsed = JSON.parse(rawJson);
        const arr = Array.isArray(parsed) ? parsed
          : parsed?.fields && Array.isArray(parsed.fields) ? parsed.fields
          : null;
        if (!arr) { setParseError('Must be an array of field definitions'); return; }
        for (let i = 0; i < arr.length; i++) {
          const f = arr[i];
          if (!f?.field_name) { setParseError(`Item ${i}: missing field_name`); return; }
          if (!f?.display_name) { setParseError(`Item ${i}: missing display_name`); return; }
          if (!f?.value_type) { setParseError(`Item ${i}: missing value_type`); return; }
        }
        setParseError(null);
        inputs = resolveGroupNames(arr) as unknown as HardwareFieldDefinitionInput[];
      } catch (err) {
        setParseError(err instanceof Error ? err.message : 'Invalid JSON');
        return;
      }
    } else {
      // Validate table fields
      const invalid = fields.find(f => !f.field_name.trim() || !f.display_name.trim());
      if (invalid) {
        setServerError('All fields must have a name and display name');
        return;
      }
      // Resolve typed group names to IDs (create new groupings if needed)
      if (resolveGroupingInput) {
        for (const f of fields) {
          if (f._groupText.trim() && f.grouping_id === null) {
            try {
              const gid = await resolveGroupingInput(f._groupText);
              f.grouping_id = gid ?? null;
            } catch (err) {
              setServerError(`Failed to create group "${f._groupText}": ${err instanceof Error ? err.message : err}`);
              return;
            }
          }
        }
      }
      inputs = fields.map(f => toInput(f, groupings));
    }

    setSaving(true);
    setServerError(null);
    setLastResult(null);
    try {
      const r = await typesService.applyHardwareSchema(hardwareTypeId, inputs, { deleteMissing });
      setLastResult(r);
      onSchemaChanged();
      // Reload to get server-assigned IDs
      if (r.errors.length === 0) await loadFromServer();
    } catch (err) {
      setServerError(err instanceof Error ? err.message : 'Failed to apply');
    } finally {
      setSaving(false);
    }
  };

  const validCount = fields.filter(f => f.field_name.trim() && f.display_name.trim()).length;

  return (
    <div className="hardware-schema-json-editor">
      <div className="hardware-schema-json-header">
        <div>
          <h4>{t('deviceTypes.editSchema')}</h4>
          <div className="hardware-schema-json-meta">
            {hardwareTypeCode} · {fields.length} fields
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <button
            type="button"
            className={`btn btn-small ${rawMode ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setRawMode(!rawMode)}
            title={rawMode ? 'Switch to table editor' : 'Switch to raw JSON'}
          >
            {rawMode ? 'Table' : 'JSON'}
          </button>
          <button type="button" className="btn btn-secondary btn-small" onClick={onClose}>
            Close
          </button>
        </div>
      </div>

      {serverError && <div className="schema-editor-error">{serverError}</div>}

      {loading ? (
        <div className="schema-editor-loading">Loading…</div>
      ) : rawMode ? (
        /* ── Raw JSON mode ── */
        <>
          <textarea
            className="hardware-schema-json-textarea"
            value={rawJson}
            onChange={(e) => { setRawJson(e.target.value); if (parseError) setParseError(null); }}
            spellCheck={false}
            readOnly={!canEdit}
            rows={18}
          />
          {parseError && <div className="schema-editor-error">Parse error: {parseError}</div>}
        </>
      ) : (
        /* ── Table mode ── */
        <div className="schema-editor-table-wrap">
          <datalist id="schema-editor-groups">
            {groupings.map(g => <option key={g.id} value={g.name} />)}
          </datalist>
          <table className="schema-editor-table">
            <thead>
              <tr>
                <th className="col-order">#</th>
                <th className="col-name">{t('devices.fieldName')}</th>
                <th className="col-display">{t('hardwareSchema.fieldDisplayName')}</th>
                <th className="col-type">Type</th>
                <th className="col-default">Default</th>
                <th className="col-group">Group</th>
                <th className="col-req">Req</th>
                <th className="col-req" title={t('deviceTypes.exposeFlagJsonEditor')}>{t('deviceTypes.expose')}</th>
                <th className="col-req" title={t('deviceTypes.wireRootTitle')}>{t('deviceTypes.wireRootColumn')}</th>
                <th className="col-actions"></th>
              </tr>
            </thead>
            <tbody>
              {fields.map((field, idx) => (
                <tr key={field._key} className={!field.field_name.trim() ? 'row-invalid' : ''}>
                  <td className="col-order">
                    <div className="order-controls">
                      <button
                        className="order-btn"
                        onClick={() => moveField(field._key, -1)}
                        disabled={idx === 0}
                        title={t('common.moveUp')}
                      >&#x25B2;</button>
                      <button
                        className="order-btn"
                        onClick={() => moveField(field._key, 1)}
                        disabled={idx === fields.length - 1}
                        title={t('common.moveDown')}
                      >&#x25BC;</button>
                    </div>
                  </td>
                  <td className="col-name">
                    <input
                      type="text"
                      value={field.field_name}
                      onChange={(e) => updateField(field._key, {
                        field_name: e.target.value.replace(/[^a-zA-Z0-9_.]/g, ''),
                      })}
                      placeholder="field_name"
                      disabled={!canEdit}
                      className="cell-input mono"
                    />
                  </td>
                  <td className="col-display">
                    <input
                      type="text"
                      value={field.display_name}
                      onChange={(e) => updateField(field._key, { display_name: e.target.value })}
                      placeholder={t('hardwareSchema.fieldDisplayName')}
                      disabled={!canEdit}
                      className="cell-input"
                    />
                  </td>
                  <td className="col-type">
                    <select
                      value={field.value_type}
                      onChange={(e) => updateField(field._key, { value_type: e.target.value })}
                      disabled={!canEdit}
                      className="cell-select"
                      style={{ color: getTypeBadgeColor(field.value_type) }}
                    >
                      {VALUE_TYPES.map(t => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                  </td>
                  <td className="col-default">
                    <input
                      type="text"
                      value={field.default_value}
                      onChange={(e) => updateField(field._key, { default_value: e.target.value })}
                      placeholder="—"
                      disabled={!canEdit}
                      className="cell-input mono"
                    />
                  </td>
                  <td className="col-group">
                    <input
                      type="text"
                      list="schema-editor-groups"
                      value={field._groupText}
                      onChange={(e) => {
                        const text = e.target.value;
                        const match = groupings.find(g =>
                          g.name.toLowerCase() === text.toLowerCase() ||
                          g.code.toLowerCase() === text.toLowerCase()
                        );
                        updateField(field._key, {
                          _groupText: text,
                          grouping_id: match ? match.id : null,
                        });
                      }}
                      placeholder="—"
                      disabled={!canEdit}
                      className="cell-input"
                    />
                  </td>
                  <td className="col-req">
                    <input
                      type="checkbox"
                      checked={field.is_required}
                      onChange={(e) => updateField(field._key, { is_required: e.target.checked })}
                      disabled={!canEdit}
                    />
                  </td>
                  <td className="col-req">
                    <input
                      type="checkbox"
                      checked={field.expose_on_wire}
                      onChange={(e) => updateField(field._key, { expose_on_wire: e.target.checked })}
                      disabled={!canEdit}
                      title={t('deviceTypes.exposeFlagCheckbox')}
                    />
                  </td>
                  <td className="col-req">
                    <input
                      type="checkbox"
                      checked={field.wire_root}
                      onChange={(e) => updateField(field._key, { wire_root: e.target.checked })}
                      disabled={!canEdit}
                      title={t('deviceTypes.wireRootTitle')}
                    />
                  </td>
                  <td className="col-actions">
                    {canEdit && (
                      <button
                        className="btn-icon btn-icon-danger"
                        onClick={() => removeField(field._key)}
                        title={t('common.removeField')}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="3 6 5 6 21 6"/>
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                        </svg>
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {fields.length === 0 && (
                <tr>
                  <td colSpan={9} className="empty-row">No fields — click + Add Field</td>
                </tr>
              )}
            </tbody>
          </table>
          {canEdit && (
            <button className="schema-editor-add-btn" onClick={addField} type="button">
              + Add Field
            </button>
          )}
        </div>
      )}

      <div className="hardware-schema-json-controls">
        <label className="hardware-schema-json-checkbox">
          <input
            type="checkbox"
            checked={deleteMissing}
            onChange={(e) => setDeleteMissing(e.target.checked)}
            disabled={!canEdit || saving}
          />
          Delete fields not in this list
        </label>
        <div className="hardware-schema-json-actions">
          <button type="button" className="btn btn-secondary btn-small" onClick={loadFromServer} disabled={saving}>
            Reload
          </button>
          <button
            type="button"
            className="btn btn-primary btn-small"
            onClick={handleApply}
            disabled={!canEdit || saving || loading || (!rawMode && validCount === 0)}
          >
            {saving ? 'Applying…' : `Apply (${rawMode ? '?' : validCount})`}
          </button>
        </div>
      </div>

      {lastResult && (
        <div className={`hardware-schema-json-result ${lastResult.errors.length > 0 ? 'partial' : 'success'}`}>
          <strong>{lastResult.errors.length > 0 ? 'Applied with errors' : 'Applied'}</strong>
          <ul>
            <li><strong>{lastResult.created.length}</strong> created{lastResult.created.length > 0 && `: ${lastResult.created.join(', ')}`}</li>
            <li><strong>{lastResult.updated.length}</strong> updated{lastResult.updated.length > 0 && `: ${lastResult.updated.join(', ')}`}</li>
            {lastResult.deleted.length > 0 && <li><strong>{lastResult.deleted.length}</strong> deleted: {lastResult.deleted.join(', ')}</li>}
            {lastResult.skipped.length > 0 && <li><strong>{lastResult.skipped.length}</strong> skipped: {lastResult.skipped.join(', ')}</li>}
          </ul>
          {lastResult.errors.length > 0 && (
            <ul className="hardware-schema-json-errors">
              {lastResult.errors.map((e, i) => <li key={i}><code>{e.field_name}</code>: {e.error}</li>)}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default HardwareSchemaJsonEditor;
